import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { Accordion, AccordionDetails, AccordionSummary, CardMedia, MenuItem, SelectChangeEvent, Stack, Tooltip, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    averageCatchmentElevation,
    catchmentArea,
    closureSectionElevation,
    curveNumber,
    dischargePD,
    dischargeType,
    flowConcentrationTime,
    mainChannelLength,
    parameterA,
    parameterN,
    rainfallHeight,
    returnPeriod,
} from '../../../conversion/discharge';
import inputRangeDischarge from '../../../inputRange/discharge';
import { cleanImage } from '../../../store/Image/imageSlice';
import { cleanAlert } from '../../../store/alert/alertSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { selectProject, setDischarge } from '../../../store/project/projectSlice';
import { cleanResult } from '../../../store/result/resultSlice';
import DropDown from '../../component/DropDown';
import Input from '../../component/Input';
import { EDischargeType } from '../../enum';
import { IInputDischarge, TInputDischarge } from '../../iProject';
import variable from '../../style/variable';
import { getInputDicharge } from '../util';
import {
    handleAverageCatchmentElevationChange,
    handleCatchmentAreaChange,
    handleClosureSectionElevationChange,
    handleCurveNumberChange,
    handleFlowConcentrationTimeChange,
    handleMainChannelLengthChange,
    handleParameterAChange,
    handleParameterNChange,
    handleRainfallHeightChange,
} from './calculationHelper';
import curveNumberImage from './curveNumberImage.png';

interface IInputDischargeProps {}

const InputDischarge = (props: IInputDischargeProps) => {
    const project = useAppSelector(selectProject);
    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    let timer = React.useRef<NodeJS.Timeout>();

    let input = getInputDicharge(project.data?.discharge!);

    const formik = useFormik<IInputDischarge>({
        initialValues: input,
        onSubmit: values => {},
    });

    const handleChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let valueAsNumber = parseFloat(value);
        let updateValue = { [name]: valueAsNumber };
        if (!isNaN(valueAsNumber)) {
            let discharge = { ...formik.values };
            switch (name) {
                case catchmentArea:
                    handleCatchmentAreaChange(value, discharge);
                    break;
                case mainChannelLength:
                    handleMainChannelLengthChange(value, discharge);
                    break;
                case averageCatchmentElevation:
                    handleAverageCatchmentElevationChange(value, discharge);
                    break;
                case closureSectionElevation:
                    handleClosureSectionElevationChange(value, discharge);
                    break;
                case parameterA:
                    handleParameterAChange(value, discharge);
                    break;
                case parameterN:
                    handleParameterNChange(value, discharge);
                    break;
                case flowConcentrationTime:
                    handleFlowConcentrationTimeChange(value, discharge);
                    break;
                case curveNumber:
                    handleCurveNumberChange(value, discharge);
                    break;
                case rainfallHeight:
                    handleRainfallHeightChange(value, discharge);
                    break;
            }
            setValues({ ...discharge, ...updateValue });
            dispatch(cleanResult());
            dispatch(cleanImage());
            dispatch(cleanAlert());
        }
    };

    const setValues = (payload: IInputDischarge) => {
        formik.setValues(payload);
        dispatch(setDischarge({ ...project.data?.discharge, ...payload }));
    };

    const handleHidden = (name: string) => {
        if (name == dischargeType) return true;
        if (
            name == catchmentArea ||
            name == mainChannelLength ||
            name == averageCatchmentElevation ||
            name == closureSectionElevation ||
            name == returnPeriod ||
            name == parameterA ||
            name == parameterN ||
            name == flowConcentrationTime ||
            name == rainfallHeight ||
            name == curveNumber
        )
            return formik.values.dischargeType == EDischargeType.Simple;

        return false;
    };

    const handleDisabled = (name: string) => {
        return false;
    };

    const handleTextTooltip = (name: string) => {
        if (name == parameterA) return t('parameterAInfobox');
        if (name == parameterN) return t('parameterNInfobox');
        if (name == flowConcentrationTime) return t('flowConcentrationTimeInfobox');
        if (name == curveNumber) return <CardMedia sx={{ height: 406, width: 500 }} image={curveNumberImage} title={t('curveNumber')} />;
    };

    return (
        <Accordion disableGutters>
            <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                <Typography>{t('dischargeInput')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <DropDown label={t('selectMethod')} labelId={`dischargeType-label`} id={'dischargeType'} name={'dischargeType'} value={formik.values.dischargeType} onChange={handleChange}>
                    <MenuItem value={EDischargeType.Simple}>{t('totalDrainageDischargeOpt')}</MenuItem>
                    <MenuItem value={EDischargeType.Correction}>{t('globalFlowCalculation')}</MenuItem>
                </DropDown>
                <Stack variant='bottomLineTextGreen' flexDirection={'row'} alignItems={'center'} gap={1}>
                    <Typography>{formik.values.dischargeType == EDischargeType.Simple ? t('totalDrainageDischargeOpt') : t('globalFlowCalculation')}</Typography>
                </Stack>
                {Object.keys(formik.values).map(
                    x =>
                        x !== dischargeType && (
                            <>
                                {x == returnPeriod && formik.values.dischargeType == EDischargeType.Correction && (
                                    <Stack variant='bottomLineTextGreen' flexDirection={'row'} alignItems={'center'} gap={1}>
                                        <Typography>{t('rainfallCurve')}</Typography>
                                        <Tooltip title={t('rainfallCurveInfobox')}>
                                            <InfoIcon style={{ fill: variable.green, height: '18px' }} fontSize='small' />
                                        </Tooltip>
                                    </Stack>
                                )}
                                <Stack key={x}>
                                    <Input
                                        hidden={handleHidden(x)}
                                        disabled={handleDisabled(x)}
                                        value={formik.values[x as TInputDischarge]}
                                        minValue={inputRangeDischarge[x]?.min.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                        maxValue={inputRangeDischarge[x]?.max.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                        id={x}
                                        isNumber={typeof formik.values[x as TInputDischarge] == 'number'}
                                        label={t(x, { unitOfMeasure: dischargePD[x as TInputDischarge]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] })}
                                        name={x}
                                        onChange={handleChange}
                                        textTooltip={handleTextTooltip(x as TInputDischarge)}
                                    />
                                </Stack>
                            </>
                        ),
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default InputDischarge;
